import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { DocumentTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { DocumentSubjectType, DocumentTypeFilters, DocumentTypeOrder, SortOrder } from '@/types'
import { Button } from '@/components/base/button'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'
import { dedupe } from '@/helpers/dedupe'
import { useTable } from '@/helpers/useTable'
import { SubjectBadge } from './badge'

interface Props extends TableProps {
  filters?: DocumentTypeFilters
  createable?: boolean
}

export const Table = ({ pageSize = 20, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<DocumentTypesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.documentTypes?.pageInfo, onIndex, basePath: '/settings/document-types' })

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<DocumentTypeFilters, DocumentTypeOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.documentTypes.nodes = dedupe([
              ...previousData.documentTypes.nodes,
              ...fetchMoreResult.documentTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, subjectEq: value as DocumentSubjectType })),
            type: 'select',
            options: Object.keys(DocumentSubjectType).map(subject => (
              { value: subject, display: <SubjectBadge subject={subject as DocumentSubjectType} /> }
            ))
          }
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Subject' },
        ]}
        rows={data.documentTypes.nodes.map(documentType => (
          {
            id: documentType.id,
            columns: [
              documentType.name,
              <SubjectBadge subject={documentType.subject} />,
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
